import { useAuth } from "@/lib/auth-store";
import type { TokenResponse } from "@/lib/types";

const API_BASE = "/api/v1";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

type Method = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

interface RequestOptions {
  method?: Method;
  body?: unknown;
  auth?: boolean;
}

let refreshing: Promise<boolean> | null = null;

/** Troca o cookie de refresh por um access token novo. Chamadas simultâneas compartilham a mesma promessa. */
export function refreshSession(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, { method: "POST", credentials: "include" });
      if (!res.ok) {
        useAuth.getState().clear();
        return false;
      }
      useAuth.getState().setSession((await res.json()) as TokenResponse);
      return true;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

async function errorMessage(res: Response): Promise<string> {
  try {
    const data = await res.json();
    if (typeof data.detail === "string") return data.detail;
    if (Array.isArray(data.detail) && data.detail[0]?.msg) return "Verifique os campos: " + data.detail[0].msg;
  } catch {
    /* corpo não-JSON */
  }
  if (res.status >= 500) return "Erro no servidor. Tente novamente em instantes.";
  return "Não foi possível concluir a ação.";
}

export async function api<T>(path: string, { method = "GET", body, auth = true }: RequestOptions = {}, retry = true): Promise<T> {
  const token = useAuth.getState().accessToken;
  const headers: Record<string, string> = {};
  const isForm = body instanceof FormData;
  if (body !== undefined && !isForm) headers["Content-Type"] = "application/json";
  if (auth && token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    credentials: "include",
    body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
  });

  if (res.status === 401 && auth && retry && (await refreshSession())) {
    return api<T>(path, { method, body, auth }, false);
  }
  if (!res.ok) throw new ApiError(res.status, await errorMessage(res));
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export async function logout() {
  try {
    await fetch(`${API_BASE}/auth/logout`, { method: "POST", credentials: "include" });
  } finally {
    useAuth.getState().clear();
  }
}

/** Arquivos protegidos (mídia do WhatsApp): baixa com o token e devolve uma URL local (blob:). */
export async function fetchBlobUrl(path: string, retry = true): Promise<string> {
  const token = useAuth.getState().accessToken;
  const res = await fetch(`${API_BASE}${path}`, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: "include" });
  if (res.status === 401 && retry && (await refreshSession())) return fetchBlobUrl(path, false);
  if (!res.ok) throw new ApiError(res.status, "Arquivo indisponível");
  return URL.createObjectURL(await res.blob());
}
